import { Alert, Button, Col, Form, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import {
  addRoadmapCompany,
  deleteRoadmapCompany,
  getRoadmapCompany,
  getCompanies,
  getRoadmaps,
} from "../repository/data.repository";
import { RoadmapCompany } from "../repository/data.default";
import { createSearchRequest } from "../hooks/useSearchRequest";
import DynamicForm from "../controls/DynamicForm";
import { DynamicSchema } from "../controls/DynamicForm.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";

export interface RoadmapCompanyViewProps {
  readonly uid?: string | null;
  readonly open?: boolean;
  readonly mode: "add" | "delete";
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
}

const roadmapsRequest = createSearchRequest(getRoadmaps, "name");
const companiesRequest = createSearchRequest(getCompanies, "name");

const assignSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "roadmap",
          label: "Roadmap",
          request: roadmapsRequest,
          rules: [{ required: true, message: "Roadmap is required" }],
        },
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: companiesRequest,
          rules: [{ required: true, message: "Company is required" }],
        },
      ],
    },
  ],
};

// Minimal assign / unassign surface for a roadmap↔company. Progress control + assessment review
// live in the dedicated RoadmapAssignmentManage component.
function RoadmapCompanyView(props: RoadmapCompanyViewProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [item, setItem] = useState<RoadmapCompany | null>(null);

  useEffect(() => {
    if (props.open) {
      if (props.mode === "delete" && props.uid) {
        getRoadmapCompany(props.uid).then(setItem).catch(() => setItem(null));
      } else {
        setItem(null);
        form.resetFields();
      }
    } else {
      reset();
    }
  }, [props.open, props.uid]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setValid(true))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function reset(): void {
    setValid(false);
    setError(false);
    setLoading(false);
    if (props.open) form.resetFields();
  }

  function onSubmit(): void {
    setLoading(true);
    setError(false);
    if (props.mode === "delete") {
      deleteRoadmapCompany(props.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
      return;
    }
    addRoadmapCompany({
      roadmapUid: values.roadmap?.value,
      companyUid: values.company?.value,
    })
      .then(() => {
        setLoading(false);
        props.onFinish?.();
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }

  function onCancel(): void {
    props.onClose?.();
  }

  return (
    <InModal
      open={props.open}
      title={modalTitle(props.mode, "Assignment", item?.companyName)}
      onCancel={onCancel}
      size={"small"}
      actions={
        <Space className="sticky bottom-0 flex flex-row items-center justify-end">
          <Button disabled={loading} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type={"primary"}
            danger={props.mode === "delete"}
            loading={loading}
            disabled={!valid && props.mode !== "delete"}
            onClick={onSubmit}
          >
            {props.mode === "add" ? "Assign" : "Delete"}
          </Button>
        </Space>
      }
    >
      <Col className="flex flex-col">
        {props.mode === "add" && (
          <DynamicForm className="w-auto" form={form} schema={assignSchema} />
        )}
        {props.mode === "delete" && (
          <Typography.Paragraph>
            <Typography.Text>
              Delete{" "}
              <Typography.Text className="font-bold">{item?.companyName}</Typography.Text>{" "}
              from this roadmap? Their progress will be removed.
            </Typography.Text>
          </Typography.Paragraph>
        )}
        {error && (
          <Alert className="mb-2" message="Something went wrong, try again" type="error" />
        )}
      </Col>
    </InModal>
  );
}

export default RoadmapCompanyView;
